import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../lib/api';
import { useNotifications } from '../../hooks/useNotifications';

interface Specialty {
  id: string;
  name: string;
  selected: boolean;
}

const AVAILABLE_SPECIALTIES = [
  { id: 'shoulder', name: 'Shoulder' },
  { id: 'hip', name: 'Hip' },
  { id: 'knee', name: 'Knee' },
  { id: 'foot_ankle', name: 'Foot & Ankle' },
];

export default function JointSpecialty() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();
  const [specialties, setSpecialties] = useState<Specialty[]>(
    AVAILABLE_SPECIALTIES.map(s => ({ ...s, selected: false }))
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCurrentPreferences();
  }, []);

  const loadCurrentPreferences = async () => {
    try {
      setLoading(true);
      const response = await apiService.request<{ tags: Array<{ id: number; text: string }> }>('/api/v1/user/tag_preferences');

      if (response.tags && response.tags.length > 0) {
        const tagTexts = response.tags.map(tag => (tag.text || '').toLowerCase());
        const hasFootAndAnkle = tagTexts.some(
          text => text === 'foot & ankle' || text === 'foot' || text === 'ankle'
        );

        setSpecialties(prev =>
          prev.map(spec => ({
            ...spec,
            selected: spec.id === 'foot_ankle'
              ? hasFootAndAnkle
              : tagTexts.includes(spec.id.toLowerCase()),
          }))
        );
      }
    } catch (error) {
      console.error('Failed to load tag preferences:', error);
      showError('Failed to Load', 'Failed to load current preferences');
    } finally {
      setLoading(false);
    }
  };

  const toggleSpecialty = (id: string) => {
    setSpecialties(prev =>
      prev.map(spec =>
        spec.id === id ? { ...spec, selected: !spec.selected } : spec
      )
    );
  };

  const handleSave = async () => {
    const selectedTags = specialties
      .filter(s => s.selected)
      .flatMap(s => {
        if (s.id === 'foot_ankle') {
          // Backend expects a single combined tag
          return [{ text: 'foot & ankle' }];
        }
        return [{ text: s.id }];
      }); // Backend expects { text: 'tagName' } format

    if (selectedTags.length === 0) {
      showError('No Selection', 'Please select at least one joint specialty.');
      return;
    }

    try {
      setSaving(true);
      await apiService.request('/api/v1/user/update_tag_preferences', {
        method: 'POST',
        body: JSON.stringify({ tags: selectedTags }),
      });

      showSuccess('Success', 'Joint specialties updated successfully!');
      setTimeout(() => navigate('/settings'), 1000);
    } catch (error) {
      console.error('Failed to update tag preferences:', error);
      showError('Update Failed', 'Failed to update joint specialties. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="flex flex-col items-center justify-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-healthy-green"></div>
          <p className="mt-4 theme-text-secondary">Loading your preferences...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/settings')}
          className="flex items-center theme-text-secondary hover:theme-text mb-4 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Settings
        </button>
        <h1 className="text-3xl font-bold theme-text mb-2">Joint Specialties</h1>
        <p className="theme-text-secondary">
          Select the areas you specialize in. This helps us show you relevant content.
        </p>
      </div>

      {/* Specialties Grid */}
      <div className="space-y-3 mb-6">
        {specialties.map((specialty) => (
          <button
            key={specialty.id}
            onClick={() => toggleSpecialty(specialty.id)}
            className={`w-full flex items-center p-4 rounded-lg border-2 transition-all ${
              specialty.selected
                ? 'border-healthy-green bg-healthy-green bg-opacity-10'
                : 'border-gray-200 dark:border-gray-700 theme-surface hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <div
              className={`w-6 h-6 rounded border-2 flex items-center justify-center mr-3 transition-colors ${
                specialty.selected
                  ? 'bg-healthy-green border-healthy-green'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
            >
              {specialty.selected && (
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
            <span
              className={`text-lg ${
                specialty.selected
                  ? 'text-healthy-green font-semibold'
                  : 'theme-text'
              }`}
            >
              {specialty.name}
            </span>
          </button>
        ))}
      </div>

      {/* Info Box */}
      {/* Save Button */}
      <div className="flex gap-3">
        <button
          onClick={() => navigate('/settings')}
          className="flex-1 px-6 py-3 border-2 border-gray-300 dark:border-gray-600 theme-text rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex-1 px-6 py-3 bg-healthy-green text-white rounded-lg font-semibold hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </button>
      </div>
    </div>
  );
}
